import { AlertTriangle, ArrowRefresh2, Vector } from 'blaze-ui/icons'
import { type OpenModalFunction } from 'almanac-ui/Modal/Types'
import type { ScheduledPostShow } from 'api/clients/ScheduledPostsClient'
import { Icon } from 'blaze-ui/icons/Types'
import { IntegrationsModal } from 'components/IntegrationsModal/IntegrationsModal'
import { INTEGRATION_CONFIGS } from 'components/IntegrationsBanner/integrationConfig'
import type { ExternalIntegration } from 'api/clients/ExternalIntegrationsClient'
import type { ScheduledPostTarget } from 'types'
import { BRAND_KITS_PATH } from 'paths'
import { generatePath } from 'react-router-dom'

export interface PostNotificationBannerConfig {
  isRendered: (scheduledPost: ScheduledPostShow, additionalData?: any) => boolean
  message: string | ((additionalData?: any) => string)
  secondaryMessage?: string | ((additionalData?: any) => string)
  testId?: string
  Icon?: Icon
  variant?: 'default' | 'warning'
  actionButton?: {
    text: string
    onPress: (params: { scheduledPostId?: number; openModal: OpenModalFunction; additionalData?: any }) => void
    variant?: 'primary' | 'secondary' | 'tertiary'
    frontIcon?: Icon
    endIcon?: Icon
    testId?: string
  }
}

export const getProviderName = (integration: ExternalIntegration) => {
  const configEntry = Object.values(INTEGRATION_CONFIGS).find((config) => config.provider === integration.provider)
  return (
    configEntry?.displayName ||
    integration.provider.charAt(0).toUpperCase() + integration.provider.slice(1).replace('_', ' ')
  )
}

export const postNotificationBannerConfigs: PostNotificationBannerConfig[] = [
  {
    isRendered: (scheduledPost, additionalData) => {
      const { disconnectedIntegrations } = additionalData || {}

      // Don't show auth warning if no disconnected integrations
      if (!disconnectedIntegrations?.length) {
        return false
      }

      // PRIORITY CHECK: If any FAILED targets have disconnected integrations, show auth banner
      // This must be checked BEFORE checking if post is "Posted" because user needs to
      // reauth first before they can retry the failed targets, even if other targets succeeded
      if (scheduledPost?.scheduled_post_targets?.length) {
        const hasFailedTargetWithDisconnectedAuth = scheduledPost.scheduled_post_targets.some(
          (target: ScheduledPostTarget) => {
            const isFailed = target.status === 'failed'
            const hasDisconnectedIntegration = disconnectedIntegrations.some(
              (integration: ExternalIntegration) => integration.id === target.external_integration_id,
            )
            return isFailed && hasDisconnectedIntegration
          },
        )
        if (hasFailedTargetWithDisconnectedAuth) {
          return true
        }
      }

      // Don't show auth warning if the post itself is already successfully posted
      // Check both 'published' (API status) and 'Posted' (property status)
      // The status can be either type depending on where the data comes from
      const postStatus = scheduledPost?.status as string
      if (postStatus === 'published' || postStatus === 'Posted') {
        return false
      }

      // Don't show auth warning if ALL targets are already successfully published
      // Even if auth expired after publishing, the mission is accomplished
      if (scheduledPost?.scheduled_post_targets?.length > 0) {
        const allTargetsPublished = scheduledPost.scheduled_post_targets.every(
          (target: ScheduledPostTarget) => target.status === 'published',
        )
        if (allTargetsPublished) {
          return false
        }
      }

      // Check if the post's main external_integration is disconnected
      if (scheduledPost?.external_integration) {
        const postIntegration = scheduledPost.external_integration
        const isPostIntegrationDisconnected = disconnectedIntegrations.some(
          (integration: ExternalIntegration) => integration.id === postIntegration.id,
        )
        if (isPostIntegrationDisconnected) {
          return true
        }
      }

      // Also check if any scheduled_post_targets have disconnected integrations
      // This handles cross-post scenarios where individual targets may be disconnected
      if (scheduledPost?.scheduled_post_targets?.length) {
        const hasDisconnectedTarget = scheduledPost.scheduled_post_targets.some((target: ScheduledPostTarget) => {
          return disconnectedIntegrations.some(
            (integration: ExternalIntegration) => integration.id === target.external_integration_id,
          )
        })
        if (hasDisconnectedTarget) {
          return true
        }
      }

      return false
    },
    message: (additionalData) => {
      const { disconnectedIntegrations, scheduledPost } = additionalData || {}

      // Collect disconnected integrations that have FAILED or PENDING targets
      // Don't include integrations where all targets successfully published
      const relevantDisconnectedIntegrations: ExternalIntegration[] = []

      // Check scheduled_post_targets for disconnected integrations with failed/pending targets
      if (scheduledPost?.scheduled_post_targets?.length && disconnectedIntegrations) {
        scheduledPost.scheduled_post_targets.forEach((target: ScheduledPostTarget) => {
          // Only include if target is not successfully published
          const isTargetUnsuccessful = target.status !== 'published'

          const disconnectedTargetIntegration = disconnectedIntegrations.find(
            (integration: ExternalIntegration) => integration.id === target.external_integration_id,
          )

          if (disconnectedTargetIntegration && isTargetUnsuccessful) {
            // Avoid duplicates
            if (!relevantDisconnectedIntegrations.find((i) => i.id === disconnectedTargetIntegration.id)) {
              relevantDisconnectedIntegrations.push(disconnectedTargetIntegration)
            }
          }
        })
      }

      // Fallback: Check main external_integration if no targets found
      // This handles single-target posts or posts without target data
      if (
        relevantDisconnectedIntegrations.length === 0 &&
        scheduledPost?.external_integration &&
        disconnectedIntegrations
      ) {
        const postIntegration = scheduledPost.external_integration
        const isPostIntegrationDisconnected = disconnectedIntegrations.find(
          (integration: ExternalIntegration) => integration.id === postIntegration.id,
        )
        if (isPostIntegrationDisconnected) {
          relevantDisconnectedIntegrations.push(isPostIntegrationDisconnected)
        }
      }

      // Generate message based on relevant disconnected integrations
      if (relevantDisconnectedIntegrations.length === 1) {
        const providerName = getProviderName(relevantDisconnectedIntegrations[0])
        return `Your ${providerName} connection expired. Publishing will fail unless you refresh your connection.`
      }

      // Multiple disconnected integrations - list them all
      const providerNames = relevantDisconnectedIntegrations.map((integration: ExternalIntegration) =>
        getProviderName(integration),
      )
      const formattedList =
        providerNames.length === 2
          ? providerNames.join(' and ')
          : `${providerNames.slice(0, -1).join(', ')}, and ${providerNames[providerNames.length - 1]}`
      return `Your ${formattedList} connections have expired. Publishing will fail unless you refresh your connections.`
    },
    testId: 'integration-reauth-settings',
    Icon: AlertTriangle,
    variant: 'warning',
    actionButton: {
      text: 'Refresh Connection',
      onPress: ({ openModal, additionalData }) => {
        const { disconnectedIntegrations, initiateOauth, scheduledPost } = additionalData || {}
        if (!disconnectedIntegrations || !initiateOauth) return

        // Collect disconnected integrations that have FAILED or PENDING targets
        // Don't include integrations where all targets successfully published
        const relevantDisconnectedIntegrations: ExternalIntegration[] = []

        // Check scheduled_post_targets for disconnected integrations with failed/pending targets
        if (scheduledPost?.scheduled_post_targets?.length) {
          scheduledPost.scheduled_post_targets.forEach((target: ScheduledPostTarget) => {
            // Only include if target is not successfully published
            const isTargetUnsuccessful = target.status !== 'published'

            const disconnectedTargetIntegration = disconnectedIntegrations.find(
              (integration: ExternalIntegration) => integration.id === target.external_integration_id,
            )

            if (disconnectedTargetIntegration && isTargetUnsuccessful) {
              // Avoid duplicates
              if (!relevantDisconnectedIntegrations.find((i) => i.id === disconnectedTargetIntegration.id)) {
                relevantDisconnectedIntegrations.push(disconnectedTargetIntegration)
              }
            }
          })
        }

        // Fallback: Check main external_integration if no targets found
        // This handles single-target posts or posts without target data
        if (relevantDisconnectedIntegrations.length === 0 && scheduledPost?.external_integration) {
          const postIntegrationDisconnected = disconnectedIntegrations.find(
            (integration: ExternalIntegration) => integration.id === scheduledPost.external_integration.id,
          )
          if (postIntegrationDisconnected) {
            relevantDisconnectedIntegrations.push(postIntegrationDisconnected)
          }
        }

        // If only one relevant disconnected integration, initiate OAuth directly
        if (relevantDisconnectedIntegrations.length === 1) {
          initiateOauth(relevantDisconnectedIntegrations[0].provider, relevantDisconnectedIntegrations[0].id)
        } else {
          // Multiple disconnected integrations - open modal to manage all
          openModal(IntegrationsModal, {})
        }
      },
      variant: 'tertiary',
      frontIcon: ArrowRefresh2,
      testId: 'refresh-connection-button',
    },
  },
  {
    isRendered: (scheduledPost, additionalData) => {
      const { disconnectedIntegrations } = additionalData || {}
      const failedTargets = scheduledPost?.scheduled_post_targets?.filter(
        (target: ScheduledPostTarget) => target.status === 'failed',
      )

      if (!failedTargets || failedTargets.length === 0 || scheduledPost?.status === 'published') {
        return false
      }

      // Don't show failed target banner if any failed targets have disconnected integrations
      // The auth banner should show instead (it has higher priority)
      if (disconnectedIntegrations?.length) {
        const hasFailedTargetWithDisconnectedAuth = failedTargets.some((target: ScheduledPostTarget) => {
          return disconnectedIntegrations.some(
            (integration: ExternalIntegration) => integration.id === target.external_integration_id,
          )
        })
        if (hasFailedTargetWithDisconnectedAuth) {
          return false
        }
      }

      return true
    },
    message: (additionalData) => {
      const { scheduledPost } = additionalData || {}
      const failedTargets = scheduledPost?.scheduled_post_targets?.filter(
        (target: ScheduledPostTarget) => target.status === 'failed',
      )

      if (!failedTargets || failedTargets.length === 0) {
        return 'Some platforms failed to post.'
      }

      const targetsWithErrors = failedTargets.filter(
        (target: ScheduledPostTarget) => target.error_class && target.error_message,
      )

      if (targetsWithErrors.length > 0) {
        // Show all errors from all failed targets
        const errorMessages = targetsWithErrors.map((target: ScheduledPostTarget) => {
          const platformName = target.provider?.charAt(0).toUpperCase() + target.provider?.slice(1) || 'Platform'
          return `${platformName} - ${target.error_message}`
        })

        return errorMessages.join('\n')
      }

      if (failedTargets.length === 1) {
        return 'A platform failed to post. Please check the details below.'
      }
      return `${failedTargets.length} platforms failed to post. Please check the details below.`
    },
    secondaryMessage: (additionalData) => {
      const { scheduledPost } = additionalData || {}
      const failedTargets = scheduledPost?.scheduled_post_targets?.filter(
        (target: ScheduledPostTarget) => target.status === 'failed',
      )

      const targetsWithErrors = failedTargets?.filter(
        (target: ScheduledPostTarget) => target.error_class && target.error_message,
      )

      // Only show the instruction if there are targets with error details
      if (targetsWithErrors && targetsWithErrors.length > 0) {
        return 'Please try posting again once the error has been fixed.'
      }

      return ''
    },
    testId: 'target-validation-error-settings',
    Icon: AlertTriangle,
    variant: 'warning',
  },
  {
    isRendered: (scheduledPost, additionalData) => {
      const { brandKit } = additionalData || {}

      if (!brandKit) {
        return false
      }

      if (!scheduledPost?.media_objects.some((object) => object.mime_type?.startsWith('video/'))) {
        return false
      }

      if (brandKit.content_preferences?.media_priority !== 'only_my_brand_kit') {
        return false
      }

      if (brandKit.media_files.some((file: any) => file.mime_type?.startsWith('video/'))) {
        return false
      }

      return true
    },
    actionButton: {
      text: 'Add Video',
      onPress: ({ additionalData }) => {
        const { workspaceId } = additionalData
        window.open(`${generatePath(BRAND_KITS_PATH, { workspaceId })}#images_videos`, '_blank')
      },
      variant: 'secondary',
      endIcon: Vector,
      testId: 'add-video-button',
    },
    message: 'To use your own video in all posts, you must add videos to your Brand Kit',
    testId: 'no-brand-kit-videos-warning',
    variant: 'default',
  },
]
